import React, { useState, useCallback, Suspense } from 'react';
import CompanyName from './CompanyName';
import TickerLogo from './common/TickerLogo';
import './PortfolioTable.css';
import ScoringExplainer from './ScoringExplainer';
import { MacroEnergyPanel, ExportControls, PortfolioRow } from './portfolio';
import ShareGrade from './portfolio/ShareGrade';
import { displayTestName } from '../utils/interpretTestData';

// lightweight-charts only matters once someone opens the chart modal.
const StockChart = React.lazy(() => import('./StockChart'));

/**
 * Enhanced Portfolio Table - Orchestrator Component
 *
 * Holds all state and helper functions, delegates rendering to sub-components:
 * - MacroEnergyPanel: Standalone macro energy analysis section
 * - ExportControls: Export format selector + download button
 * - PortfolioRow: Individual table rows with expandable detail panels
 * - PortfolioExpandedRow: Test breakdown panels (used inside PortfolioRow)
 *
 * Also renders the table header, scoring scale legends, loading/empty states,
 * and the StockChart modal.
 */

const PortfolioTable = ({ items, scores, loading, analysisResult, onExport }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  // Map of symbol -> { name, data } so each holding row has its own
  // independent test-info selection. The macro panel uses the reserved
  // key "__macro__" for its own (single) selection.
  const [selectedTests, setSelectedTests] = useState({});
  const [showEnergyBreakdown, setShowEnergyBreakdown] = useState({});
  const [chartSymbol, setChartSymbol] = useState(null);

  // Standalone Macro Energy state
  const [standaloneEnergy, setStandaloneEnergy] = useState(null);
  const [energyLoading, setEnergyLoading] = useState(false);
  const [energyError, setEnergyError] = useState(null);
  const [showStandaloneEnergy, setShowStandaloneEnergy] = useState(false);

  // Fetch standalone macro energy data
  const fetchMacroEnergy = useCallback(async () => {
    setEnergyLoading(true);
    setEnergyError(null);
    try {
      const response = await fetch('/api/portfolio/macro-energy');
      if (!response.ok) {
        throw new Error(`Failed to fetch macro energy: ${response.status}`);
      }
      const data = await response.json();

      // Transform the API response into the format our UI expects
      const tests = Object.entries(data.tests || {}).map(([name, test]) => ({
        name,
        score: test.score_0to1 || 0.5,
        weight: data.weights?.[name] || 0.15,
        interpretation: test.explanation || `Score: ${(test.score_percent || 50).toFixed(0)}%`,
        raw_data: test.raw || {},
        computable: test.computable !== false
      }));

      setStandaloneEnergy({
        energy_score: data.score_0to5,
        regime: data.regime,
        risk_budget_multiplier: data.risk_budget_multiplier,
        summary: data.overall_explanation,
        computed_at: data.computed_at,
        tests,
        turbulence: data.turbulence || null,
      });
      setShowStandaloneEnergy(true);
    } catch (error) {
      console.error('Macro energy fetch error:', error);
      setEnergyError(error.message);
    } finally {
      setEnergyLoading(false);
    }
  }, []);

  // Create a map of scores by symbol for quick lookup
  const scoresMap = {};
  scores.forEach(score => {
    scoresMap[score.symbol] = score;
  });

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatScore = (score) => {
    if (score === null || score === undefined) return '-';
    return score.toFixed(2);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const getScoreColor = (score, maxScore) => {
    if (score === null || score === undefined) return '#6b7280';
    const percentage = (score / maxScore) * 100;
    if (percentage >= 66) return '#6495ED';  // Cyan blue
    if (percentage >= 33) return '#FBBF24';  // Bright yellow
    return '#EF4444';  // Red
  };

  const getBandColor = (band) => {
    // Outlined conviction badges: colored border + text, transparent bg
    switch (band) {
      case 'High':
        return { border: '#6495ED', text: '#6495ED' };  // Cyan blue (SCALE 78-100)
      case 'High-Medium':
        return { border: '#10B981', text: '#10B981' };  // Emerald (HOLD/SCALE 72-78)
      case 'Medium':
        return { border: '#FBBF24', text: '#FBBF24' };  // Bright yellow (HOLD 57-72)
      case 'Medium-Low':
        return { border: '#FB923C', text: '#FB923C' };  // Light orange (TRIM/HOLD 48-57)
      case 'Low':
        return { border: '#EF4444', text: '#EF4444' };  // Red (TRIM 0-48)
      default:
        return { border: '#6b7280', text: '#6b7280' };
    }
  };

  const toggleRow = (symbol) => {
    // Clear THIS symbol's test selection on toggle. Other rows' selections
    // (and the macro panel's selection under "__macro__") are unaffected,
    // so a comparison view across multiple holdings can keep different
    // tests open per row.
    setSelectedTests(prev => {
      if (!(symbol in prev)) return prev;
      const next = { ...prev };
      delete next[symbol];
      return next;
    });
    setExpandedRows(prev => ({ ...prev, [symbol]: !prev[symbol] }));
  };

  const handleTestClick = (symbol, testName, testData, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setSelectedTests(prev => {
      const next = { ...prev };
      if (next[symbol]?.name === testName) {
        delete next[symbol];
      } else {
        next[symbol] = { name: testName, data: testData };
      }
      return next;
    });
  };

  // Macro panel uses the reserved "__macro__" key. Keep its existing
  // selectedTest / setSelectedTest API by giving it scoped accessors.
  const macroSelectedTest = selectedTests.__macro__ || null;
  const setMacroSelectedTest = (testOrNull) => {
    setSelectedTests(prev => {
      const next = { ...prev };
      if (testOrNull) {
        next.__macro__ = testOrNull;
      } else {
        delete next.__macro__;
      }
      return next;
    });
  };

  const toggleEnergyBreakdown = (symbol, e) => {
    e.stopPropagation();
    setShowEnergyBreakdown(prev => ({
      ...prev,
      [symbol]: !prev[symbol]
    }));
  };

  const handleExport = async () => {
    if (!items.length) return;

    setExportLoading(true);
    try {
      const response = await fetch(`/api/portfolio/snapshot/export?format=${exportFormat}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          positions: items.map(item => ({
            symbol: item.symbol,
            shares: item.shares,
            entry_price: item.entry_price,
            current_price: item.current_price
          })),
          include_crypto: true
        }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `portfolio_export.${exportFormat}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export portfolio: ' + error.message);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="portfolio-table-container">
      {/* Standalone Macro Energy Section */}
      <MacroEnergyPanel
        standaloneEnergy={standaloneEnergy}
        energyLoading={energyLoading}
        energyError={energyError}
        showStandaloneEnergy={showStandaloneEnergy}
        fetchMacroEnergy={fetchMacroEnergy}
        setShowStandaloneEnergy={setShowStandaloneEnergy}
        selectedTest={macroSelectedTest}
        setSelectedTest={setMacroSelectedTest}
        getScoreColor={getScoreColor}
        turbulence={standaloneEnergy?.turbulence || analysisResult?.turbulence}
      />

      <div className="table-header">
        <h2>Portfolio Analysis</h2>

        <ExportControls
          exportFormat={exportFormat}
          setExportFormat={setExportFormat}
          handleExport={handleExport}
          exportLoading={exportLoading}
          loading={loading}
          hasData={items.length > 0 && scores.length > 0}
        />
      </div>

      {loading && (
        <div className="loading">Calculating scores...</div>
      )}

      {items.length > 0 && (
        <>
          <ScoringExplainer />
          <div className="table-wrapper">
            <table className="portfolio-table">
            <thead>
              <tr className="column-group-row">
                <th colSpan="7"></th>
                <th colSpan="3" className="column-group-label group-start-scores">Stock Tests</th>
                <th className="column-group-label group-start-conviction">Score</th>
                <th className="column-group-label group-start-action">Action</th>
              </tr>
              <tr>
                <th className="expand-col"></th>
                <th>Symbol</th>
                <th>Shares</th>
                <th>Entry</th>
                <th>Current</th>
                <th>P&L %</th>
                <th>Value</th>
                <th className="group-start-scores" title="Quality Score: Measures fundamentals — growth, margins, ROIC, and competitive moat. Higher = stronger business.">Qual</th>
                <th title="Setup Score: Measures technical positioning — trend, momentum, relative strength, and volume. Higher = better entry timing.">Setup</th>
                <th title="Convex Score: Measures asymmetric upside potential — catalysts, demand signals, and asymmetric risk/reward. 0-5 scale.">Cvx</th>
                <th className="group-start-conviction" title="Conviction Score: Weighted blend of Quality, Setup, and Convex. Weights adjust continuously based on market conditions — more Quality in stress, more Setup in momentum.">Conviction</th>
                <th className="group-start-action" title="Recommended action based on conviction score and market conditions.">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const score = scoresMap[item.symbol];
                const isExpanded = expandedRows[item.symbol];

                return (
                  <PortfolioRow
                    key={index}
                    item={item}
                    score={score}
                    isExpanded={isExpanded}
                    toggleRow={toggleRow}
                    getScoreColor={getScoreColor}
                    getBandColor={getBandColor}
                    formatCurrency={formatCurrency}
                    formatScore={formatScore}
                    formatPercent={formatPercent}
                    setChartSymbol={setChartSymbol}
                    selectedTests={selectedTests}
                    handleTestClick={handleTestClick}
                    showEnergyBreakdown={showEnergyBreakdown}
                    toggleEnergyBreakdown={toggleEnergyBreakdown}
                    index={index}
                    turbulence={analysisResult?.turbulence}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Card Layout (shown on small screens via CSS) */}
        <div className="mobile-portfolio-cards">
          {items.map((item, index) => {
            const score = scoresMap[item.symbol];
            const pnlPct = item.entry_price > 0
              ? ((item.current_price - item.entry_price) / item.entry_price) * 100
              : 0;
            const value = (item.shares || 0) * (item.current_price || 0);
            const isExpanded = expandedRows[item.symbol];
            const conv = score?.conviction || {};
            const mobileAction = conv.recommended_action ?? score?.recommended_action ?? score?.short_term_action ?? '-';
            const mobileBand = conv.conviction_band ?? score?.conviction_band;
            const bandColors = getBandColor(mobileBand);
            const isCrypto = score?.is_crypto || score?.data_quality?.is_crypto;
            const isETF = score?.is_etf || score?.data_quality?.is_etf;
            const isMutualFund = score?.is_mutual_fund || score?.data_quality?.is_mutual_fund;
            const qualityNotApplicable = isCrypto || isETF || isMutualFund;
            const qualityNAReason = qualityNotApplicable
              ? (isCrypto
                  ? "Quality scoring measures company fundamentals (margins, balance sheet, cash flow). Digital assets don't have these."
                  : isETF
                    ? "Quality scoring measures company fundamentals. ETFs are baskets — quality lives at the holdings level."
                    : "Quality scoring measures company fundamentals. Mutual funds are baskets — quality lives at the holdings level.")
              : '';

            return (
              <div
                key={index}
                className={`mobile-card ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleRow(item.symbol)}
              >
                <div className="mobile-card-header">
                  {/* 2026-09-23: the mobile card never had a logo. It is a
                      separate render path from the desktop <td>, so the logo
                      added there simply never reached a phone. Same root cause
                      as the desktop-only per-test breakdown (9/11) and overlay
                      chart (9/12); the shared component is what stops it
                      happening to the next one. */}
                  <div className="mobile-card-identity">
                    <TickerLogo symbol={item.symbol} isCrypto={isCrypto} />
                    <span className="co-idcol">
                      <span className="mobile-card-symbol">{item.symbol}</span>
                      <CompanyName symbol={item.symbol} crypto={!!isCrypto} />
                    </span>
                  </div>
                  <span
                    className="mobile-card-action"
                    style={{ color: bandColors.text, border: `1px solid ${bandColors.border}`, background: 'transparent' }}
                  >
                    {mobileAction}
                  </span>
                </div>

                <div className="mobile-card-metrics">
                  <div className="mobile-metric">
                    <span className="mobile-metric-label">Price</span>
                    <span className="mobile-metric-value">{formatCurrency(item.current_price)}</span>
                  </div>
                  <div className="mobile-metric">
                    <span className="mobile-metric-label">P&L</span>
                    <span className={`mobile-metric-value ${pnlPct >= 0 ? 'positive' : 'negative'}`}>
                      {formatPercent(pnlPct)}
                    </span>
                  </div>
                  <div className="mobile-metric">
                    <span className="mobile-metric-label">Value</span>
                    <span className="mobile-metric-value">{formatCurrency(value)}</span>
                  </div>
                </div>

                {/* Conviction hero — matches screener card pattern */}
                <div className="mobile-card-hero">
                  <div className="mobile-card-hero-score">
                    <span className="mobile-card-hero-value">
                      {(conv.combined_conviction ?? score?.combined_conviction) != null ? (conv.combined_conviction ?? score?.combined_conviction).toFixed(0) : '—'}
                    </span>
                    <span className="mobile-card-hero-max">/100</span>
                  </div>
                  <div className="mobile-card-hero-meta">
                    <span className="mobile-card-hero-label">Conviction</span>
                    <span
                      className="mobile-card-hero-band"
                      style={{ color: bandColors.text }}
                    >
                      {score?.conviction_band || '—'}
                    </span>
                  </div>
                </div>

                <div className="mobile-card-scores">
                  <span className="mobile-score-pill" title={qualityNotApplicable ? qualityNAReason : undefined}>
                    <span className="pill-label">Quality</span>
                    {qualityNotApplicable ? (
                      <>
                        <span className="pill-value pill-value-na">N/A</span>
                        <span className="pill-max" />
                      </>
                    ) : (
                      <>
                        <span className="pill-value" style={{ color: getScoreColor(score?.stock_quality_score, 100) }}>
                          {formatScore(score?.stock_quality_score)}
                        </span>
                        <span className="pill-max">/100</span>
                      </>
                    )}
                  </span>
                  <span className="mobile-score-pill">
                    <span className="pill-label">Setup</span>
                    <span className="pill-value" style={{ color: getScoreColor(score?.stock_setup_score, 100) }}>
                      {formatScore(score?.stock_setup_score)}
                    </span>
                    <span className="pill-max">/100</span>
                  </span>
                  <span className="mobile-score-pill">
                    <span className="pill-label">Convex</span>
                    <span className="pill-value" style={{ color: getScoreColor(score?.convex_radar_score, 5) }}>
                      {formatScore(score?.convex_radar_score)}
                    </span>
                    <span className="pill-max">/5</span>
                  </span>
                  <span className="mobile-score-pill">
                    <span className="pill-label">Energy</span>
                    <span className="pill-value" style={{ color: getScoreColor(score?.energy_matrix_score, 5) }}>
                      {formatScore(score?.energy_matrix_score)}
                    </span>
                    <span className="pill-max">/5</span>
                  </span>
                </div>

                {isExpanded && score && (
                  <div className="mobile-card-expand" onClick={(e) => e.stopPropagation()}>
                    {/* 2026-09-25: the candlestick chart was reachable ONLY from
                        the desktop expanded row's "Price chart" button, so on a
                        phone there was no way to open it at all — he went
                        looking for the new candles and SMA overlays and could
                        not find them. Fourth time a feature has been
                        desktop-only for the same reason (per-test breakdown
                        9/11, overlay chart 9/12, ticker logos 9/23). */}
                    <button
                      type="button"
                      className="mcx-chart-btn"
                      onClick={() => setChartSymbol(item.symbol)}
                    >
                      📈 Price chart, candles and moving averages
                    </button>

                    {/* Simplified mobile breakdown — tests as labeled list,
                        no bar charts, no radar, no thesis. Progressive
                        disclosure via native <details> for each pillar. */}
                    <details className="mcx-pillar">
                      <summary>
                        <span>Quality tests</span>
                        <span className="mcx-pillar-score" title={qualityNotApplicable ? qualityNAReason : undefined}>
                          {qualityNotApplicable ? 'N/A' : `${formatScore(score.stock_quality_score)}/100`}
                        </span>
                      </summary>
                      {score.quality_pillars && (
                        <div className="mcx-test-list">
                          {Object.entries(score.quality_pillars).map(([name, test]) => (
                            <div key={name} className="mcx-test-row">
                              <span className="mcx-test-label">{displayTestName(name)}</span>
                              <span className={`mcx-test-value ${test?.computable === false ? 'na' : ''}`}>
                                {test?.computable !== false
                                  ? `${((test?.score_0to1 ?? 0.5) * 100).toFixed(0)}%`
                                  : 'N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </details>

                    <details className="mcx-pillar">
                      <summary>
                        <span>Setup tests</span>
                        <span className="mcx-pillar-score">
                          {formatScore(score.stock_setup_score)}/100
                        </span>
                      </summary>
                      {score.setup_pillars && (
                        <div className="mcx-test-list">
                          {Object.entries(score.setup_pillars).map(([name, test]) => (
                            <div key={name} className="mcx-test-row">
                              <span className="mcx-test-label">{displayTestName(name)}</span>
                              <span className={`mcx-test-value ${test?.computable === false ? 'na' : ''}`}>
                                {test?.computable !== false
                                  ? `${((test?.score_0to1 ?? 0.5) * 100).toFixed(0)}%`
                                  : 'N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </details>

                    <details className="mcx-pillar">
                      <summary>
                        <span>Convex tests</span>
                        <span className="mcx-pillar-score">
                          {formatScore(score.convex_radar_score)}/5
                        </span>
                      </summary>
                      {score.convex_tests && (
                        <div className="mcx-test-list">
                          {Object.entries(score.convex_tests).map(([name, test]) => (
                            <div key={name} className="mcx-test-row">
                              <span className="mcx-test-label">{displayTestName(name)}</span>
                              <span className={`mcx-test-value ${test?.computable === false ? 'na' : ''}`}>
                                {test?.computable !== false
                                  ? `${((test?.score_0to1 ?? 0.5) * 100).toFixed(0)}%`
                                  : 'N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </details>

                    {score.energy_tests && Object.keys(score.energy_tests).length > 0 && (
                      <details className="mcx-pillar">
                        <summary>
                          <span>Macro energy tests</span>
                          <span className="mcx-pillar-score">
                            {formatScore(score.energy_matrix_score)}/5
                          </span>
                        </summary>
                        <div className="mcx-test-list">
                          {Object.entries(score.energy_tests).map(([name, test]) => (
                            <div key={name} className="mcx-test-row">
                              <span className="mcx-test-label">{displayTestName(name)}</span>
                              <span className={`mcx-test-value ${test?.computable === false ? 'na' : ''}`}>
                                {test?.computable !== false
                                  ? `${((test?.score_0to1 ?? 0.5) * 100).toFixed(0)}%`
                                  : 'N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    <p className="mcx-desktop-hint">
                      Radar view, AI thesis, earnings summary, and full test drill-downs are available on desktop or laptop.
                    </p>
                  </div>
                )}
                {/* Share / copy the public grade card — at the bottom of the
                    mobile card (like the Screener). stopPropagation so tapping it
                    doesn't toggle the card's expand. */}
                <div className="mobile-card-share" onClick={(e) => e.stopPropagation()}>
                  <ShareGrade
                    symbol={item.symbol}
                    conviction={conv.combined_conviction ?? score?.combined_conviction}
                    label={mobileBand}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Three Scoring Scale Legends */}
        <div className="scoring-scales-container">
          {/* Scale 1: Quality & Setup Scores (0-100) */}
          <div className="scoring-scale-legend">
            <h4>📊 Quality & Setup Score Thresholds (0-100)</h4>
            <div className="scale-container">
              <div className="scale-visual">
                <div className="scale-segment scale-low" style={{flex: '33'}}>
                  <span className="scale-label">LOW</span>
                  <span className="scale-range">0-33</span>
                </div>
                <div className="scale-segment scale-mid" style={{flex: '33'}}>
                  <span className="scale-label">MID</span>
                  <span className="scale-range">33-66</span>
                </div>
                <div className="scale-segment scale-high" style={{flex: '34'}}>
                  <span className="scale-label">HIGH</span>
                  <span className="scale-range">66-100</span>
                </div>
              </div>
              <div className="scale-explanation">
                <span>Weak ←</span>
                <span>Quality & Setup Pillar Scores</span>
                <span>→ Strong</span>
              </div>
            </div>
          </div>

          {/* Scale 2: Convex & Energy Scores (0-5) */}
          <div className="scoring-scale-legend">
            <h4>🎯 Convex Score Thresholds (0-5)</h4>
            <div className="scale-container">
              <div className="scale-visual">
                <div className="scale-segment scale-low" style={{flex: '30'}}>
                  <span className="scale-label">LOW</span>
                  <span className="scale-range">0-1.5</span>
                </div>
                <div className="scale-segment scale-mid" style={{flex: '50'}}>
                  <span className="scale-label">MID</span>
                  <span className="scale-range">1.5-4</span>
                </div>
                <div className="scale-segment scale-high" style={{flex: '20'}}>
                  <span className="scale-label">HIGH</span>
                  <span className="scale-range">4-5</span>
                </div>
              </div>
              <div className="scale-explanation">
                <span>Avoid ←</span>
                <span>Convex Opportunity & Macro Energy</span>
                <span>→ Scale</span>
              </div>
            </div>
          </div>

          {/* Scale 3: Conviction Scores (0-100 -> Action) - 5 tier */}
          <div className="scoring-scale-legend">
            <h4>Conviction Score Thresholds</h4>
            <div className="scale-container">
              <div className="scale-visual">
                <div className="scale-segment scale-trim" style={{flex: '48'}}>
                  <span className="scale-label">Unfavorable</span>
                  <span className="scale-range">0-48</span>
                </div>
                <div className="scale-segment scale-trim-hold" style={{flex: '9'}}>
                  <span className="scale-label">Cautious</span>
                  <span className="scale-range">48-57</span>
                </div>
                <div className="scale-segment scale-hold" style={{flex: '15'}}>
                  <span className="scale-label">Neutral</span>
                  <span className="scale-range">57-72</span>
                </div>
                <div className="scale-segment scale-hold-scale" style={{flex: '6'}}>
                  <span className="scale-label">Promising</span>
                  <span className="scale-range">72-78</span>
                </div>
                <div className="scale-segment scale-scale" style={{flex: '22'}}>
                  <span className="scale-label">Favorable</span>
                  <span className="scale-range">78-100</span>
                </div>
              </div>
              <div className="scale-explanation">
                <span>Lower conviction ←</span>
                <span>Conviction Score (adaptive weighting based on market conditions)</span>
                <span>→ Higher conviction</span>
              </div>
            </div>
          </div>

          {/* Scale 4: Crypto Sentiment Thresholds */}
          <div className="scoring-scale-legend">
            <h4>₿ Crypto Sentiment Thresholds</h4>
            <div className="scale-container">
              <div className="scale-visual">
                <div className="scale-segment scale-bearish" style={{flex: '33'}}>
                  <span className="scale-label">BEARISH</span>
                  <span className="scale-range">&lt;40</span>
                </div>
                <div className="scale-segment scale-neutral" style={{flex: '34'}}>
                  <span className="scale-label">NEUTRAL</span>
                  <span className="scale-range">40-60</span>
                </div>
                <div className="scale-segment scale-bullish" style={{flex: '33'}}>
                  <span className="scale-label">BULLISH</span>
                  <span className="scale-range">&gt;60</span>
                </div>
              </div>
              <div className="scale-explanation">
                <span>Reduce ←</span>
                <span>Crypto Technical Sentiment Score</span>
                <span>→ Add</span>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {items.length === 0 && !loading && (
        <div className="empty-state">
          {/* 2026-09-11: this used to read "Upload a portfolio CSV to begin
              analysis", which demanded a file most people do not have and never
              mentioned that My Portfolio above accepts typed positions. */}
          <p>Add your holdings in <strong>My Portfolio</strong> above to grade them. A CSV works too, if you have one.</p>
        </div>
      )}

      {chartSymbol && (
        <Suspense fallback={<div aria-busy="true" />}>
          <StockChart symbol={chartSymbol} onClose={() => setChartSymbol(null)} />
        </Suspense>
      )}
    </div>
  );
};

export default PortfolioTable;
